import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, ControllerRenderProps } from "react-hook-form";
import { Option, Icon } from "uikit";
import { isBoolean } from "lodash";

import { useTypedSelector } from "../../../../../../../../redux/store";
import { Schema } from "../../../../../../../../redux/constants/OrdersPage/order";
import { useAppContext } from "../../../../../../../../context";
import {
	useCheckFlagAccessControl,
	useTaxiServiceFilterAccessControl,
} from "../../../../../../../../hooks";
import {
	StyledRow,
	StyledSpan,
	Text,
	StyledWrapIcon,
	Divider,
	Select,
} from "../../../../../../../../components/common";
import { useOrderModalContext } from "../../../../../context";
import { OrderFormProps } from "../../types/FormProps";
import {
	TransportationType,
	transportationTypes,
} from "../../types/transportationTypes";
import { TabAccessNames, TabKeys } from "../../constants/access";

type Fn = (type: TransportationType) => Option<TransportationType>;

interface RenderTransportationType {
	field: ControllerRenderProps<Schema, "transportationType">;
}
interface RenderCompanyID {
	field: ControllerRenderProps<Schema, "companyID">;
}

interface RenderTaxiServiceId {
	field: ControllerRenderProps<Schema, "taxiServiceId">;
}
const Left: React.FC<OrderFormProps> = ({ tab, form, disabled }) => {
	const { t } = useTranslation();
	const { dateFns, getTaxiServiceIds } = useAppContext();
	const checkFlagAccessControl = useCheckFlagAccessControl(TabAccessNames);
	const {
		nextElementById,
		language,
		setIsChangTaxiService,
		setIsChangCompany,
	} = useOrderModalContext();
	const { accessTaxiServices, accessCompaniesIds, accessTaxiServicesIds } =
		useTaxiServiceFilterAccessControl<any>();

	const { rates } = useTypedSelector(
		(state) => state.preferencesReducer.rates,
	);

	const companiesRedux = useTypedSelector(
		(state) => state.ordersPageReducer.companies,
	);

	const { models: serviceModels } = useTypedSelector(
		(state) => state.preferencesReducer.services.data,
	);

	const [city, setCity] = useState<string>("");

	const companies = useMemo(() => {
		const retval =
			companiesRedux.items?.filter((item) =>
				accessCompaniesIds.includes(item.id || -1),
			) || [];
		return retval;
	}, [accessCompaniesIds, companiesRedux.items]);

	const taxiServices = useMemo(
		() => accessTaxiServices,
		[accessTaxiServices],
	);

	const currentCompanyId = useMemo(
		() => tab?.form?.companyID,
		[tab?.form?.companyID],
	);

	const isExecutor = useMemo(() => {
		const value = tab.form?.executorSip;
		return !!value;
	}, [tab.form?.executorSip]);

	const taxiServiceId = useMemo(
		() => tab?.form?.taxiServiceId,
		[tab?.form?.taxiServiceId],
	);

	const hasPhone = useMemo(() => tab?.form?.phone, [tab?.form?.phone]);
	const orderServices = useMemo(
		() => tab?.form?.services || [],
		[tab?.form?.services],
	);
	const taxiClass = useMemo(() => tab.form.taxiClass, [tab.form.taxiClass]);

	const handleSave = useCallback(() => {
		form.setValue("orderSave", true);
		form.setValue("orderPointSave", false);
		if (hasPhone) {
			nextElementById("1street");
		} else {
			nextElementById("customer-phone-id");
		}
	}, [form, nextElementById, hasPhone]);

	const companyToOption = useCallback(
		(company) => ({
			key: company.id,
			value: company.id,
			label: company.name?.[language],
		}),
		[language],
	);

	const companiesOptions = useMemo(
		() => companies.map(companyToOption),
		[companyToOption, companies],
	);

	const taxiServiceToOption = useCallback(
		(service) => ({
			key: service.id,
			value: service.id,
			label: service.settlement?.[language],
		}),
		[language],
	);

	const taxiServiceOptions = useMemo(() => {
		const selectedCompanyId = currentCompanyId;
		const selectedCompany = companies?.find(
			(item) => item.id === selectedCompanyId,
		);

		return (
			selectedCompany?.taxiServices
				?.filter((item) => accessTaxiServicesIds.includes(item.id))
				?.map(taxiServiceToOption) || []
		);
	}, [
		currentCompanyId,
		companies,
		taxiServiceToOption,
		accessTaxiServicesIds,
	]);

	const wrap = useCallback<Fn>(
		(type) => ({
			key: type,
			value: type,
			label: t([
				`orderPage.order_form.transportation_types.${type}`,
				type,
			]),
		}),
		[t],
	);

	const options = useMemo(
		() => transportationTypes.map((type) => wrap(type)),
		[wrap],
	);

	const onRenderCreatedAt = useCallback(
		({ field }: { field: ControllerRenderProps<Schema, "createdAt"> }) => {
			const text = `${t([
				`orderPage.order_form.order_time.title`,
				"Added",
			])}: `;

			return (
				<Text
					textStyle={{
						w: "auto",
					}}
					text={text}
					value={dateFns.formatTime2(field.value, "HH:mm:ss", {
						timezone: dateFns.timezone,
					})}
				/>
			);
		},
		[dateFns, t],
	);

	const onRenderStatus = useCallback(
		({ field }: { field: ControllerRenderProps<Schema, "status"> }) => {
			const text = `${t([
				`order_status.${field.value}`,
				"Order status",
			])}`;

			return (
				<StyledSpan
					m="0 10px 0 10px"
					font={{ size: "10px", line: "12px", fw: "600" }}
					colors="#8000FF"
					bgC="#F0E0FF"
					br="4px"
					p="2px 4px"
					h="16px"
				>
					{text}
				</StyledSpan>
			);
		},
		[t],
	);

	const onRenderTransportationType = useCallback(
		({ field }: RenderTransportationType) => {
			const value = wrap(field.value || "passenger").key;
			const selected = options
				.map((item, i) => {
					if (value === item.value) return i + 1;
					return null;
				})
				.find((item) => item !== null);

			return (
				<Select
					selected={selected || 0}
					disabled={
						disabled ||
						isExecutor ||
						!checkFlagAccessControl[TabKeys.TRANSPORTATION_TYPE]
					}
					options={options}
					value={value}
					onSelect={(option) => {
						field.onChange({
							target: { value: option?.value },
						} as unknown as React.ChangeEvent<Element>);
						handleSave();
					}}
				/>
			);
		},
		[
			wrap,
			options,
			disabled,
			isExecutor,
			checkFlagAccessControl,
			handleSave,
		],
	);

	const getDefaultCity = useCallback(
		(id?: number) => {
			const foundedOption = [...taxiServices]
				.filter((item) => item.id === id)
				.find((item) => item.id === id);

			const defaultCity = {
				id: foundedOption?.id,
				value: foundedOption?.settlement?.[language] || "",
				settlement: foundedOption?.settlement?.[language] || "",
				coordinates: foundedOption?.coordinates as any,
			};

			return defaultCity;
		},

		[taxiServices, language],
	);

	const onDefaultCity = useCallback(
		(id: number) => {
			const defaultCity = getDefaultCity(id);
			setCity(defaultCity.value);
			tab.setState({ ...tab.state, defaultCity });
			handleSave();
		},
		[getDefaultCity, tab, handleSave],
	);

	const cityName = useMemo(() => {
		if (!city) return "";
		return tab?.state?.defaultCity?.value || "";
	}, [city, tab?.state?.defaultCity?.value]);

	useEffect(() => {
		if (taxiServiceId) onDefaultCity(taxiServiceId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [taxiServiceId, language]);

	const setDefaultRateId = useCallback(() => {
		const rate =
			rates.find((item) => item.default && item.active) ||
			rates.find((item) => item.root === true);

		form.setValue("rateId", rate?.id || 1);
		const rateIncludePickupCost =
			rate?.additionalFields?.general?.autoCalculatePickupCost;
		const autoCalculateRoundTrip =
			rate?.additionalFields?.general?.autoCalculateRoundTrip;

		if (isBoolean(rateIncludePickupCost)) {
			form.setValue("rateIncludePickupCost", rateIncludePickupCost);
		}
		if (isBoolean(autoCalculateRoundTrip)) {
			form.setValue("autoCalculateRoundTrip", autoCalculateRoundTrip);
		}
	}, [rates, form]);

	const changedRateId = useCallback(
		(tsId: number, companyId?: number) => {
			const carClassIds = taxiClass?.map((item) => item.value) || [];
			const foundRates = rates.filter((rate) => {
				if (!rate?.active) return false;
				if (rate?.root) return true;

				const cars = carClassIds
					.map((item) => rate.carClassIds.includes(item))
					.every((item) => item);

				const taxi = rate.taxiServiceIds.includes(tsId);
				const company = companyId
					? rate.taxiServices.find((service) => {
							if (service.company?.id === companyId) return true;
							return false;
					  })
					: true;

				if (taxi && company && cars) return true;
				return false;
			});
			const foundRate =
				foundRates.find((rate) => {
					if (rate.root) return false;
					if (rate.default) return false;
					if (
						rate.taxiServiceIds.length === 1 &&
						rate.carClassIds.length === 1
					)
						return true;
					return false;
				}) ||
				foundRates.find((rate) => {
					if (rate.root) return false;
					if (rate.default) return false;
					if (
						rate.taxiServiceIds.length >= 1 &&
						rate.carClassIds.length === 1
					)
						return true;
					return false;
				}) ||
				foundRates.find((rate) => {
					if (rate.root) return false;
					if (rate.default) return false;
					if (
						rate.taxiServiceIds.length === 1 &&
						rate.carClassIds.length >= 1
					)
						return true;
					return false;
				}) ||
				foundRates.find((rate) => {
					if (rate.root) return false;
					if (rate.default) return false;
					if (
						rate.taxiServiceIds.length >= 1 &&
						rate.carClassIds.length >= 1
					)
						return true;
					return false;
				}) ||
				foundRates.find((rate) => {
					if (rate.root) return false;
					if (rate.default) return true;
					return false;
				}) ||
				foundRates.find((rate) => {
					if (rate.root) return true;
					return false;
				}) ||
				foundRates.at?.(0);

			const rateIncludePickupCost =
				foundRate?.additionalFields?.general?.autoCalculatePickupCost;
			const autoCalculateRoundTrip =
				foundRate?.additionalFields?.general?.autoCalculateRoundTrip;

			const rateId: number | undefined = foundRate?.id;

			form.setValue("rateId", rateId || 1);
			if (isBoolean(rateIncludePickupCost)) {
				form.setValue("rateIncludePickupCost", rateIncludePickupCost);
			}

			if (isBoolean(autoCalculateRoundTrip)) {
				form.setValue("autoCalculateRoundTrip", autoCalculateRoundTrip);
			}
		},
		[rates, taxiClass, form],
	);

	const changedServices = useCallback(
		(taxiServiceId: number) => {
			const serviceIdsForSelectedTaxiService = serviceModels
				?.filter(
					(item) =>
						item.active &&
						item.taxiServices
							?.map((item) => item?.id)
							?.includes(taxiServiceId) === true,
				)
				.map((item) => item.id);

			const modifyServices = orderServices?.filter((item) =>
				serviceIdsForSelectedTaxiService.includes(item.value),
			);

			form.setValue("services", modifyServices);
		},
		[serviceModels, form, orderServices],
	);

	const setTaxiServiceIdIfInCompanyOne = useCallback(
		(id: number) => {
			if (!id) return;
			const list = getTaxiServiceIds(id);

			if (list && list?.length === 1) {
				const oneId = list[0];
				if (oneId) {
					onDefaultCity(oneId);
					changedRateId(oneId, id);
					changedServices(oneId);
					setIsChangTaxiService(true);
					form.setValue("taxiServiceId", oneId);
				}
			}
		},
		[
			changedRateId,
			changedServices,
			form,
			getTaxiServiceIds,
			onDefaultCity,
			setIsChangTaxiService,
		],
	);

	const onRenderCompanyIDe = useCallback(
		({ field }: RenderCompanyID) => {
			const selected = companiesOptions
				.map((item, i) => {
					if (field?.value === item.value) return i + 1;
					return null;
				})
				.find((item) => item !== null);

			return (
				<Select
					selected={selected || 0}
					disabled={
						disabled ||
						isExecutor ||
						!checkFlagAccessControl[
							TabKeys.COMPANIES_AND_TAXI_SERVICES
						]
					}
					options={companiesOptions}
					value={field.value}
					onSelect={(option) => {
						field.onChange({
							target: { value: option?.value },
						} as unknown as React.ChangeEvent<Element>);
						setIsChangCompany(true);
						if (option) setDefaultRateId();
						if (
							option?.value &&
							typeof option?.value === "number"
						) {
							setTaxiServiceIdIfInCompanyOne(option.value);
						}
					}}
				/>
			);
		},
		[
			setTaxiServiceIdIfInCompanyOne,
			checkFlagAccessControl,
			companiesOptions,
			disabled,
			isExecutor,
			setDefaultRateId,
			setIsChangCompany,
		],
	);

	const placeholderTaxiService = t(["taxiService", "Branch"]);
	const onRenderTaxiServiceId = useCallback(
		({ field }: RenderTaxiServiceId) => {
			const exist = taxiServiceOptions.find((taxi) => {
				if (field.value === taxi.value) return taxi;
				return false;
			});

			if (!exist) setCity("");
			else {
				const city = tab?.form?.city;
				if (!city) {
					if (form.getValues().city !== exist?.label)
						form.setValue("city", exist?.label);
					setCity(exist?.label || "");
				}
			}

			const selected = taxiServiceOptions
				.map((item, i) => {
					if (field?.value === item?.value) return i + 1;
					return null;
				})
				.find((item) => item !== null);

			return (
				<Select
					selected={selected}
					disabled={
						disabled ||
						isExecutor ||
						!checkFlagAccessControl[
							TabKeys.COMPANIES_AND_TAXI_SERVICES
						]
					}
					options={taxiServiceOptions}
					value={field.value}
					placeholder={placeholderTaxiService}
					onSelect={(option) => {
						if (typeof option.value === "number") {
							onDefaultCity(option.value);
							changedRateId(option.value, currentCompanyId);
							changedServices(option.value);
							setIsChangTaxiService(true);
						}

						field.onChange({
							target: { value: option?.value },
						} as unknown as React.ChangeEvent<Element>);
					}}
				/>
			);
		},

		[
			currentCompanyId,
			taxiServiceOptions,
			disabled,
			isExecutor,
			checkFlagAccessControl,
			placeholderTaxiService,
			tab?.form?.city,
			form,
			onDefaultCity,
			changedRateId,
			changedServices,
			setIsChangTaxiService,
		],
	);

	const source = useMemo(() => {
		const value = tab?.form?.source;
		const text = `${t([`order_source.${value}`, "Source"])}`;
		return text;
	}, [tab?.form?.source, t]);

	return (
		<StyledRow
			alignItems="center"
			h="clamp(26px, 3.6vh, 32px)"
			over="visible"
			flex={{ wrap: "nowrap", flex: "0 1 100%" }}
		>
			<Controller
				name="createdAt"
				control={form.control}
				render={onRenderCreatedAt}
			/>

			<Controller
				name="status"
				control={form.control}
				render={onRenderStatus}
			/>
			<Divider h="18px" />
			<Controller
				name="transportationType"
				control={form.control}
				render={onRenderTransportationType}
			/>
			<Divider h="18px" />
			<Controller
				name="companyID"
				control={form.control}
				render={onRenderCompanyIDe}
			/>
			<Divider h="18px" />
			<Controller
				name="taxiServiceId"
				control={form.control}
				render={onRenderTaxiServiceId}
			/>
			<Divider h="18px" />

			<StyledRow alignItems="center" gap="0 6px" p="0 0 0 10px">
				<StyledWrapIcon w="15px" h="15px" colors="#647079">
					<Icon id="order-modal-city" size={15} />
				</StyledWrapIcon>
				<StyledSpan colors="#647079">{cityName}</StyledSpan>
			</StyledRow>

			<StyledRow m="0 0 0 10px" alignItems="center" gap="0 6px">
				<StyledWrapIcon
					w="16px"
					h="16px"
					offset={{ x: "0", y: "2px" }}
					colors="#647079"
				>
					<Icon id="order-modal-source" size={16} />
				</StyledWrapIcon>
				<StyledSpan colors="#647079">{source}</StyledSpan>
			</StyledRow>
		</StyledRow>
	);
};

export default Left;
